import type { ParsedUrlQuery } from 'querystring';
import type { Writable } from 'type-fest';
import type { GetServerSideProps } from 'next';
import qs from 'qs';
import { camelCase, isUndefined, mapKeys, pick, omitBy, compact, mapValues } from 'lodash';
import { Form, Input } from 'antd';
import { ApolloError } from '@apollo/client';
import { useEffect, type ReactNode } from 'react';
import { parseApolloErrors } from '$apollo/util';
import { isBrowser, parseStringUrlQuery, changeUrl } from '$util/index';
import { getSegmentFromServer } from '$util/getSegment';
import { useAppContext } from '$util/AppContext';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { replaceCollectionPlaceholders } from '$cms/replaceCollectionPlaceholders';
import { generateCanonicalUrlForCollections } from '$util/generateCanonicalUrlForCollections';
import { fetchPageData } from '$server/modules/cms/fetchPageData';
import { queryCmsContent } from '$server/modules/cms';
import { getPathPlaceholders, isPreviewActive } from '$server/modules/cms/util';
import type { CmsContentProps } from '$cms/index';
import type { CmsPageContent, CollectionPathPlaceholder, CmsPageSeo } from '$cms/types';
import { collectionPathPlaceholders } from '$cms/types';
import { getLongTextByKey, getShortTextByKey } from '$cms/getDataByKey';
import { getHomepageMetadata } from '$cms/gql';
import { ContentComponentFactory } from '$cms/ContentComponentFactory';
import type { ContentComponent } from '$cms/ContentComponent';
import { CmsPageContext, useCmsPageContext } from '$cms/CmsPageContext';
import MainLayout from '$layout/MainLayout';
import { GET_DOMAIN_VERIFICATION_CONTENT, GET_HOMEPAGE_OPEN_GRAPH_CONTENT } from '$layout/MainLayout/gql';
import type { QueryReadFooterData } from '$components/layout/Footer/gql/queries/readFooterData';
import { QUERY_READ_FOOTER_DATA } from '$components/layout/Footer/gql/queries/readFooterData';
import { QUERY_GET_FOOTER_CMS_CONTENT } from '$components/layout/Footer/gql/queries/getFooterCmsContent';
import { getNavbarQuery } from '$commons/gql';
import { getPromotionBannerQuery } from '$quantum/molecules/PromotionBanner/gql';
import { getVrmLookupQuery } from '$quantum/molecules/VrmLookup/gql';
import { HighlightContentComponentErrors } from '$quantum/molecules/HighlightContentComponentErrors';
import { mergeConcat } from '$util/basic/mergeConcat';
import { getLocaleFromHostname } from '$util/domains';
import { useActiveExperiment } from '$util/abTest';
import { ActiveExperiments } from '$util/abTest/experiments';
import { container } from '@/di';
import { makeServerLogger, type ServerLogger, type ServerLoggerOnlyOnce } from '@/server/logger';
import { Button } from '@/components/atoms/Button';
import {
  seoStructuredDataModule,
  ContentComponentGetSeoStructuredDataSI,
} from '@/di/ContentComponent/seoStructuredData.di';
import type { RedirectPropsResult } from '@/types/nextjs';
import type { ReactChildrenProps } from '@/types/react';
import type { SeoStructuredData } from '@/util/seoStructuredData';
import { runInRequestContext } from '@/contexts/RequestContext';
import { CACHE_CONTROL_CACHE_ENABLED, CACHE_CONTROL_NO_CACHE } from '../internals/next/config/headers';

export const PAGE_NAME = 'dynamic-route';

/**
 * @note The loggers get initiated only in `getServerSideProps()` to avoid Client bundling.
 */
let logger: ServerLogger;
let logOnce: ServerLoggerOnlyOnce;

const ERR_NO_CMS_COMPONENTS = 'ERR_NO_CMS_COMPONENTS';

/**
 * @todo each CMS Page entity should have a SEO entity attached to it, read data from there.
 */
const useCmsPageMeta = () => {
  const {
    cmsContent: { dictionary },
  } = useAppContext<AppContextGlobal>();

  const getShortText = getShortTextByKey.bind(dictionary);
  const getLongText = getLongTextByKey.bind(dictionary);

  return {
    title: getShortText('homepage.metadata.title', 'Car service, MOT and repair the easy way | Fixter'),
    description: getLongText(
      'homepage.metadata.description',
      `Looking for hassle-free MOT, car service, repair or maintenance? ` +
        `Let Fixter take the strain! Our local experts will collect and deliver ` +
        `your car to your door and make sure you get the right job done at a fair price.`
    ),
  };
};

/**
 * CMS data transformed in typePolicies
 */
type CmsContentReadData = QueryReadFooterData;

export interface DynamicRoutePageProps extends CmsContentProps<CmsContentReadData> {
  readonly path: string;
  readonly pageData: CmsPageContent;
  readonly componentsServerSideData: Record<ContentComponent['displayName'], unknown>;
  readonly seoStructuredData?: SeoStructuredData;
  readonly preview: boolean;
}

export interface DynamicRoutePageParams extends ParsedUrlQuery {
  readonly dynamicRoute: string[];
}

export function DynamicRoutePage({
  path,
  pageData,
  componentsServerSideData,
  preview,
}: DynamicRoutePageProps): ReactNode {
  const { components = [] } = pageData;

  /**
   * If it's not preview mode we'll gather all the component errors
   * on SSR rendering and log them once at the end.
   */
  const componentErrors: Record<string, unknown> = {};

  /**
   * @NOTE temporary hardcoded list of components with configurations containing images
   *
   * @TODO standardise configuration of all UI components so this list can be deleted
   */
  const ComponentsWithConfigurableImages = ['UspBenefits', 'Help', 'Logos', 'HeroSection'];
  /**
   * NOTE: Dynamic rendering of QV components using CMS defined page data structure
   * - iterate over the content components provided by the CMS service
   * - for each content component, pick the QV component using it's 'UI Component' id
   * - get the QV components props data by extracting the middle field from their keys;
   */
  const pageContentComponents = components.flatMap((component, i) => {
    const { id, uiComponent } = component;
    try {
      const factory = new ContentComponentFactory(component);
      const { UiComponent, translatedEntities, configuration } = factory;
      if (!UiComponent) return []; // filter out unknown components
      const serverSideData = componentsServerSideData[uiComponent];

      // avoid passing undefined props
      const restProps = omitBy(
        {
          configuration:
            /**
             * @NOTE Where the component is one that has images customise the configuration
             * to ensure the correct image loading approach is taken based on the component
             * position and supplied config
             */
            ComponentsWithConfigurableImages.includes(UiComponent.name)
              ? {
                  ...configuration,
                  /**
                   * @NOTE set image loading to lazy if...
                   *
                   * a) Explicitly set by CM in the config, otherwise...
                   * b) If the component is not the first one on the page
                   */
                  lazyLoadImages: configuration?.lazyLoadImages ?? i > 1,
                  /**
                   * @NOTE preload this component's images if...
                   *
                   * a) Explicitly set by CM in the config, otherwise...
                   * b) If the component is the first one the page
                   */
                  preloadImages: configuration?.preloadImages ?? i < 1,
                }
              : configuration,
          serverSideData,
        },
        isUndefined
      );

      return (
        <UiComponent
          key={id}
          id={id}
          translatedEntities={translatedEntities}
          {...restProps}
          className={i === 0 && 'top'}
        />
      );
    } catch (error) {
      if (!preview) {
        if (!isBrowser) componentErrors[id] = error;
        return []; // filter out components with errors
      }
      return <HighlightContentComponentErrors key={id} id={id} uiComponent={uiComponent} error={error} />;
    }
  });

  /**
   * Log ContentComponents errors on server once per service start.
   * Use to avoid spamming with the same error when multiple requests hit.
   */
  if (!isBrowser && !preview && Object.keys(componentErrors).length) {
    logOnce.error(
      { path, componentErrors, '@cacheKey': `${path};${Object.keys(componentErrors).map((cid) => cid)}` },
      'Published Content Components with errors'
    );
  }

  if (!preview && !pageContentComponents.length) {
    const errMsg = 'No ContentComponents to render on dynamic route';
    if (isBrowser) {
      console.error(errMsg, { path, preview, pageData });
    } else {
      logger.error({ path, preview, pageData, reason: ERR_NO_CMS_COMPONENTS }, errMsg);
    }
    // redirect to homepage to not show a broken page
    return changeUrl({ pathname: '/' }) ?? null;
  }

  return <div className="ContentSectionContainer">{pageContentComponents}</div>;
}

function DynamicRoutePageLayout({
  seoStructuredData,
  children,
}: Pick<DynamicRoutePageProps, 'seoStructuredData'> & ReactChildrenProps): JSX.Element {
  const { location } = useAppContext();
  const cmsPageMeta = useCmsPageMeta();
  const cmsPageContext = useCmsPageContext();

  const collectionItems = cmsPageContext?.pageRoutes?.[0]?.collectionItems;
  const pathPattern = cmsPageContext?.pathPattern;
  const seo = cmsPageContext?.seo;
  const canonical =
    (collectionItems &&
      pathPattern &&
      generateCanonicalUrlForCollections(location, pathPattern, collectionItems)) ||
    undefined;

  const isMotServicingQuotesPageUK = useActiveExperiment(ActiveExperiments.MotServicingQuotesPageUK, 'B');
  useEffect(() => {
    /**
     * Hacky way to change the MOT page title for the experiment.
     * If we set it from Kameleoon, the title change only works when a user visits /mot page directly.
     */
    if (window?.document) {
      const motPageH1 = document.querySelector('.page-mot .HeroSection h1');
      if (motPageH1 && isMotServicingQuotesPageUK) {
        motPageH1.innerHTML = 'MOT from £39 with a service, made easy';
      }
    }
  }, [isMotServicingQuotesPageUK]);

  return (
    <MainLayout
      className="dynamic-page"
      cmsPageMeta={{
        pathname: location.pathname,
        ...cmsPageMeta,
        /**
         * @NOTE dirty hacky fix for KKON-169
         *
         * ensures that dynamically routed pages, which are CMS managed, will default
         * to setting meta robots with `index, follow` if SEO options block are not set on
         * for the page in the CMS.
         *
         * @TODO the right solution for this is to use NextJS layouts to provide a different
         * header component which applies this default and does not rely on this previous
         * robots implementation.
         */
        robots: true,
        canonical,
      }}
      seo={seo}
      seoStructuredData={seoStructuredData}
    >
      {children}
    </MainLayout>
  );
}

enum LayoutErrorCode {
  /**
   * When trying to preview a path with collection placeholders
   * and the collection items are missing from the URL.
   */
  PreviewPathMissingCollectionItems = 'PreviewPathMissingCollectionItems',
  /**
   * When trying to preview a path with invalid collection placeholders.
   */
  PreviewPathInvalidPlaceholders = 'PreviewPathInvalidPlaceholders',
}

type DynamicRoutePageLayoutErrorProps = {
  layoutErrorCode: LayoutErrorCode;
  path: string;
};

type DynamicRouteServerSideProps = DynamicRoutePageProps | DynamicRoutePageLayoutErrorProps;

/**
 * We can render different layouts based on what page we're on.
 */
DynamicRoutePage.getLayout = (page: JSX.Element, props: DynamicRouteServerSideProps) => {
  if ('layoutErrorCode' in props) {
    switch (props.layoutErrorCode) {
      case LayoutErrorCode.PreviewPathMissingCollectionItems: {
        return (
          <main style={{ margin: 50, textAlign: 'center' }}>
            <h1>You are trying to preview a page path with collection items.</h1>
            <h2>Please provide all the collection item IDs you want to preview in the URL.</h2>
            <Form
              name="preview-collections"
              labelCol={{ span: 10 }}
              wrapperCol={{ span: 5 }}
              autoComplete="off"
              onFinish={(placeholders) =>
                changeUrl({
                  pathname: props.path,
                  query: { preview: true, ...placeholders },
                })
              }
            >
              {getPathPlaceholders(props.path).map((collection) => (
                <Form.Item label={collection} name={collection} key={collection} rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              ))}

              <Form.Item wrapperCol={{ offset: 10, span: 1 }}>
                <Button type="submit">Preview</Button>
              </Form.Item>
            </Form>
          </main>
        );
      }
      case LayoutErrorCode.PreviewPathInvalidPlaceholders:
        return (
          <main style={{ margin: 50, textAlign: 'center' }}>
            <h1>
              STOP
              <br />
              You are trying to preview a page path with invalid collection placeholders.
            </h1>
            <p>
              Use one of these path placeholders:
              <code> {collectionPathPlaceholders.join(', ')}</code>
            </p>
          </main>
        );
      default:
        return null;
    }
  }

  const { pageData, seoStructuredData } = props;

  return (
    <CmsPageContext.Provider value={pageData}>
      <DynamicRoutePageLayout seoStructuredData={seoStructuredData}>{page}</DynamicRoutePageLayout>
    </CmsPageContext.Provider>
  );
};

export const getServerSideProps: GetServerSideProps<DynamicRouteServerSideProps> = async ({
  req,
  res,
  query: urlQuery,
  params,
  preview: isPreviewMode = false,
}) =>
  runInRequestContext(req, async () => {
    if (!logger) {
      logger = makeServerLogger(PAGE_NAME);
      logOnce = logger.onlyOnce();
      /**
       * @note makes it available for components with `fetchServerSideData` to use.
       */
      container.bind<ServerLogger>('dynamicRouteLogger').toConstantValue(logger);
    }

    const log = logger.traceRequest();

    if (!params?.dynamicRoute || !Array.isArray(params.dynamicRoute)) {
      log.error({ params }, 'Unexpected dynamic route with missing params');
      return {
        redirect: {
          permanent: false,
          destination: '/error',
        },
      };
    }
    const locale = getLocaleFromHostname(req.headers.host || '');
    const segment = getSegmentFromServer(req);
    const urlData = parseStringUrlQuery(qs.parse(urlQuery as unknown as string));
    const path = `/${params.dynamicRoute.filter((slug) => slug !== '[...dynamicRoute]').join('/')}`;

    /**
     * @NOTE 404 if the url path is for a file (i.e. image, file etc)
     *
     * 404 if the path contains a '.' followed by at least 2 characters (i.e. an extension)
     * */
    if (/^.*\..+$/.test(path) || path === '/_next/webpack-hmr') {
      return {
        notFound: true,
      };
    }

    const preview = isPreviewActive(urlData, isPreviewMode);

    log.info(
      {
        path,
        segment,
        preview,
        urlData,
        headers: pick(req.headers, ['from', 'user-agent']),
      },
      'Requested dynamic route'
    );

    try {
      /**
       * When previewing a path with collection items we need to pass the collection ids in the url path.
       */
      let previewCollection: Record<string, string> | undefined;
      if (preview) {
        let placeholders: CollectionPathPlaceholder[];
        try {
          placeholders = getPathPlaceholders(path);
        } catch (error) {
          throw Error(LayoutErrorCode.PreviewPathInvalidPlaceholders);
        }
        if (placeholders.length) {
          previewCollection = mapKeys(
            omitBy(pick(urlData, placeholders), isUndefined) as Record<string, string>,
            (v, key) => camelCase(key)
          );
          if (placeholders.length !== Object.values(previewCollection).length) {
            throw Error(LayoutErrorCode.PreviewPathMissingCollectionItems);
          }
        }
      }

      const pageDataVars = { path, segment, preview, previewCollection };
      log.debug(pageDataVars, 'Fetching page data');
      const pageData: CmsPageContent | RedirectPropsResult = await fetchPageData(pageDataVars).then(
        (data) => {
          const { collectionItems, redirect } = data.pageRoutes[0];
          if (redirect) {
            return {
              redirect: {
                statusCode: redirect.type,
                destination: redirect.path,
              },
            };
          }

          const { seo, ...otherData } = data;
          const payload: Writable<CmsPageContent> = otherData;

          if (seo) {
            const seoPayload: Writable<CmsPageSeo> = Object.fromEntries(
              Object.entries(seo).filter(([, value]) => value !== undefined)
            ) as CmsPageSeo;

            if (collectionItems) {
              const { title, metaDescription } = seo;
              if (title) seoPayload.title = replaceCollectionPlaceholders(title, collectionItems);
              if (metaDescription)
                seoPayload.metaDescription = replaceCollectionPlaceholders(metaDescription, collectionItems);
            }
            payload.seo = seoPayload;
          }

          return payload;
        }
      );

      /**
       * CMS `Page` has a `Redirect` entity and it's unpublished | archived | deleted.
       */
      if ('redirect' in pageData) {
        log.info({ path, redirect: pageData.redirect }, 'Redirecting dynamic route page');
        return pageData;
      }

      // avoid polluting logs with big objects like rich text
      const pageDataInfo = {
        id: pageData.id,
        segment: pageData.segment,
        pathPattern: pageData.pathPattern,
        revision: pageData.revision,
        pageRoutes: pageData.pageRoutes.map((route) => ({
          ...route,
          collectionItems: mapValues(route.collectionItems, (item) => item?.id),
        })),
        components: pageData.components?.map((component) => component.id),
      };
      log.info({ path, pageData: pageDataInfo }, 'Fetched dynamic route page data');

      let { components } = pageData;
      let componentsServerSideData: Record<ContentComponent['displayName'], unknown> = {};
      let componentsSeoStructuredData: SeoStructuredData[] = [];
      if (components?.length) {
        const componentsServerSideErrors: Record<string, unknown> = {};

        /**
         * Fetch server side data of ContentComponents concurrently.
         * @note we load this here because it doesn't work with App Router and it's the only place needed for now.
         */
        container.load(seoStructuredDataModule);

        componentsServerSideData = await Promise.all(
          components.map(async ({ uiComponent }) => {
            try {
              const fetchServerSideData =
                await ContentComponentFactory.importFetchServerSideData(uiComponent);
              const data = await fetchServerSideData?.(pageData, preview, container, req);
              return data ? ([uiComponent, data] as const) : undefined;
            } catch (error) {
              let errMsg = 'Unknown error';
              if (error instanceof Error) errMsg = error.message;
              else if (typeof error === 'string') errMsg = error;
              componentsServerSideErrors[uiComponent] = errMsg;
              return undefined;
            }
          })
        ).then((response) => Object.fromEntries(compact(response)));

        /**
         * Construct SEO Structured Data from components that have a `getSeoStructuredData()` function.
         */
        componentsSeoStructuredData = components.flatMap((component) => {
          try {
            const { id, uiComponent } = component;
            const getSeoStructuredData = container.getNamed(
              ContentComponentGetSeoStructuredDataSI,
              uiComponent
            );

            /**
             * @todo don't use the factory multiple times, it computes same in render too.
             */
            const factory = new ContentComponentFactory(component);
            const { UiComponent, translatedEntities, configuration } = factory;
            if (!UiComponent) return []; // filter out unknown components

            const serverSideData = componentsServerSideData[uiComponent];
            const data = getSeoStructuredData({
              id,
              pageData,
              translatedEntities,
              configuration,
              serverSideData,
              preview,
            });
            return data;
          } catch (noFnBind) {
            // filter out components that do not have this function bind
            return [];
          }
        });

        /**
         * Log errors, but exclude component to not crash the page.
         */
        const uiComponentsWithErrors = Object.keys(componentsServerSideErrors);
        if (uiComponentsWithErrors.length) {
          log.error(
            { info: { path, previewCollection, errors: componentsServerSideErrors } },
            'Errors while fetching components server side data'
          );
          components = components.filter(({ id }) => uiComponentsWithErrors.includes(id));
        }
      }

      /**
       * @note after the filter above `components` can become empty
       */
      if (!preview && !components?.length) {
        log.error(
          { path, pageData: pageDataInfo, reason: ERR_NO_CMS_COMPONENTS },
          'No CMS components for dynamic route'
        );
        // redirect to homepage to not show a broken page
        return {
          redirect: {
            statusCode: 302,
            destination: '/',
          },
        };
      }

      const cmsContent = await queryCmsContent(PAGE_NAME, {
        cmsQuery: [
          getHomepageMetadata,
          GET_HOMEPAGE_OPEN_GRAPH_CONTENT,
          GET_DOMAIN_VERIFICATION_CONTENT,
          QUERY_GET_FOOTER_CMS_CONTENT,
          getNavbarQuery,
          getVrmLookupQuery,
          getPromotionBannerQuery,
        ],
        resQuery: [QUERY_READ_FOOTER_DATA],
        context: {
          variables: { locale },
          preview,
        },
      });

      if (preview) res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
      else res.setHeader('Cache-Control', CACHE_CONTROL_CACHE_ENABLED);

      const props: Writable<DynamicRoutePageProps> = {
        path,
        pageData,
        componentsServerSideData,
        preview,
        cmsContent,
      };

      if (componentsSeoStructuredData.length) {
        props.seoStructuredData = mergeConcat(...componentsSeoStructuredData);
      }

      return { props };
    } catch (err) {
      if (err instanceof Error && err.message in LayoutErrorCode) {
        return {
          props: {
            layoutErrorCode: err.message as LayoutErrorCode,
            path,
          },
        };
      }

      if (path === '/') {
        log.error({ path, err }, 'HOMEPAGE IS REDIRECTING TO 404');
      } else if (err instanceof ApolloError) {
        const errors = parseApolloErrors(err);
        log.info({ path, err: errors }, 'Redirecting dynamic route to 404');
      } else {
        log.error({ path, err }, 'Error on dynamic route, redirecting to 404');
      }

      return {
        notFound: true,
      };
    }
  });

export default DynamicRoutePage;
